import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { isCongresoDelegado } from "../../_helpers/backofficePermissions";
import { alertError } from "../../utils/logger";
import { Card, CardBody, CardHeader } from "../../_metronic/_partials/controls";
import {
  getAssociatesStats,
  getPaymentsStats,
  getCoursesStats,
  getLeadsStats,
} from "../../api/dashboard";
import ReactApexChart from "react-apexcharts";
import {
  Users,
  DollarSign,
  BookOpen,
  TrendingUp,
  UserCheck,
  UserX,
  CreditCard,
  Calendar,
  Target,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

export function DashboardPage() {
  const user = useSelector((s) => s.authentication?.user, shallowEqual);
  const isDelegado = isCongresoDelegado(user?.role);
  const [loading, setLoading] = useState(true);
  const [associatesData, setAssociatesData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [coursesData, setCoursesData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [associates, payments, courses, leads] = await Promise.all([
        getAssociatesStats(),
        getPaymentsStats(),
        getCoursesStats(),
        getLeadsStats(),
      ]);

      setAssociatesData(associates.data);
      setPaymentsData(payments.data);
      setCoursesData(courses.data);
      setLeadsData(leads.data);
    } catch (err) {
      alertError({
        error: err,
        customMessage: "Error al cargar las estadísticas del dashboard.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDelegado) return;
    fetchAllData();
  }, [isDelegado]);

  if (isDelegado) {
    return <Redirect to="/congresos" />;
  }

  // Colores corporativos
  const primaryColor = "#f3691a"; // Naranja
  const secondaryColor = "#1e88e5"; // Azul

  // Gráfica de donut para asociados activos/inactivos
  const associatesDonutOptions = {
    chart: {
      type: "donut",
    },
    labels: ["Activos", "Inactivos"],
    colors: [primaryColor, "#e0e0e0"],
    legend: {
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
    },
  };

  const associatesDonutSeries = associatesData
    ? [associatesData.activos, associatesData.inactivos]
    : [];

  // Gráfica de línea para evolución de asociados
  const associatesLineOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor, secondaryColor],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    xaxis: {
      categories: associatesData?.evolucion?.map((e) => e.mes) || [],
    },
    yaxis: {
      title: {
        text: "Número de asociados",
      },
    },
    legend: {
      position: "top",
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  const associatesLineSeries = associatesData
    ? [
        {
          name: "Nuevos",
          data: associatesData.evolucion?.map((e) => e.nuevos) || [],
        },
        {
          name: "Activos",
          data: associatesData.evolucion?.map((e) => e.activos) || [],
        },
      ]
    : [];

  // Gráfica de línea para evolución de ingresos
  const paymentsLineOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    xaxis: {
      categories: paymentsData?.evolucionIngresos?.map((e) => e.mes) || [],
    },
    yaxis: {
      title: {
        text: "Ingresos (€)",
      },
      labels: {
        formatter: function (val) {
          return val.toFixed(0) + "€";
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val.toFixed(2) + "€";
        },
      },
    },
  };

  const paymentsLineSeries = paymentsData
    ? [
        {
          name: "Ingresos",
          data: paymentsData.evolucionIngresos?.map((e) => e.ingresos) || [],
        },
      ]
    : [];

  // Gráfica de barras para método de pago
  const paymentMethodOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor, secondaryColor],
    xaxis: {
      categories: ["IBAN", "Tarjeta"],
    },
    yaxis: {
      title: {
        text: "Número de pagos",
      },
    },
    dataLabels: {
      enabled: true,
    },
  };

  const paymentMethodSeries = paymentsData
    ? [
        {
          name: "Pagos completados",
          data: [paymentsData.pagosIBAN, paymentsData.pagosTarjeta],
        },
      ]
    : [];

  // Gráfica de donut para estado de pagos
  const paymentStatusOptions = {
    chart: {
      type: "donut",
    },
    labels: ["Completados", "Pendientes", "Fallidos"],
    colors: [primaryColor, "#ffa726", "#ef5350"],
    legend: {
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
    },
  };

  const paymentStatusSeries = paymentsData
    ? [
        paymentsData.pagosCompletados,
        paymentsData.pagosPendientes,
        paymentsData.pagosFallidos,
      ]
    : [];

  // Gráfica de barras horizontales para top cursos
  const topCoursesOptions = {
    chart: {
      type: "bar",
      horizontal: true,
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor],
    xaxis: {
      categories:
        coursesData?.topCursos?.map((c) =>
          c.titulo.length > 30 ? c.titulo.substring(0, 30) + "..." : c.titulo
        ) || [],
    },
    yaxis: {
      title: {
        text: "Inscripciones",
      },
    },
    dataLabels: {
      enabled: true,
    },
  };

  const topCoursesSeries = coursesData
    ? [
        {
          name: "Inscripciones",
          data: coursesData.topCursos?.map((c) => c.inscripciones) || [],
        },
      ]
    : [];

  // Gráfica de línea para evolución de inscripciones
  const coursesLineOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
    },
    colors: [secondaryColor],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    xaxis: {
      categories:
        coursesData?.evolucionInscripciones?.map((e) => e.mes) || [],
    },
    yaxis: {
      title: {
        text: "Número de inscripciones",
      },
    },
    tooltip: {
      shared: true,
    },
  };

  const coursesLineSeries = coursesData
    ? [
        {
          name: "Inscripciones",
          data:
            coursesData.evolucionInscripciones?.map((e) => e.inscripciones) ||
            [],
        },
      ]
    : [];

  // Gráfica de donut para estado de inscripciones
  const inscriptionStatusOptions = {
    chart: {
      type: "donut",
    },
    labels: ["Pagadas", "Pendientes", "Aprobadas"],
    colors: [primaryColor, "#ffa726", secondaryColor],
    legend: {
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
    },
  };

  const inscriptionStatusSeries = coursesData
    ? [
        coursesData.inscripcionesPagadas,
        coursesData.inscripcionesPendientes,
        coursesData.inscripcionesAprobadas,
      ]
    : [];

  // Gráfica de línea comparativa leads vs socios
  const leadsComparisonOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor, secondaryColor],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    xaxis: {
      categories: leadsData?.evolucionLeads?.map((e) => e.mes) || [],
    },
    yaxis: {
      title: {
        text: "Cantidad",
      },
    },
    legend: {
      position: "top",
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  const leadsComparisonSeries = leadsData
    ? [
        {
          name: "Leads",
          data: leadsData.evolucionLeads?.map((e) => e.leads) || [],
        },
        {
          name: "Nuevos Socios",
          data: leadsData.evolucionSocios?.map((e) => e.socios) || [],
        },
      ]
    : [];

  // Gráfica de barras para conversión mensual
  const conversionBarOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    colors: [primaryColor],
    xaxis: {
      categories: leadsData?.evolucionLeads?.map((e) => e.mes) || [],
    },
    yaxis: {
      title: {
        text: "Tasa de conversión (%)",
      },
      max: 100,
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
    },
  };

  const conversionBarSeries = leadsData
    ? [
        {
          name: "Conversión",
          data: leadsData.evolucionLeads?.map((lead, index) => {
            const socios = leadsData.evolucionSocios?.[index]?.socios || 0;
            return lead.leads > 0 ? ((socios / lead.leads) * 100).toFixed(1) : 0;
          }) || [],
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* KPIs Principales */}
      <div className="row mb-5">
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div
                  className="flex-shrink-0"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    backgroundColor: `${primaryColor}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px",
                  }}
                >
                  <Users size={30} color={primaryColor} />
                </div>
                <div className="flex-grow-1 ms-4">
                  <div className="text-muted small">Total Asociados</div>
                  <div className="h3 mb-0">{associatesData?.totalAsociados || 0}</div>
                  <div className="text-muted small mt-1">
                    {associatesData?.activos || 0} activos
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div
                  className="flex-shrink-0"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    backgroundColor: `${secondaryColor}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px",
                  }}
                >
                  <DollarSign size={30} color={secondaryColor} />
                </div>
                <div className="flex-grow-1 ms-4">
                  <div className="text-muted small">Recaudado este mes</div>
                  <div className="h3 mb-0">
                    {paymentsData?.recaudadoEsteMes?.toFixed(2) || "0.00"}€
                  </div>
                  <div className="text-muted small mt-1">
                    {paymentsData?.recaudadoEsteAnio?.toFixed(2) || "0.00"}€ este año
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div
                  className="flex-shrink-0"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    backgroundColor: `${primaryColor}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px",
                  }}
                >
                  <BookOpen size={30} color={primaryColor} />
                </div>
                <div className="flex-grow-1 ms-4">
                  <div className="text-muted small">Total Inscripciones</div>
                  <div className="h3 mb-0">{coursesData?.totalInscripciones || 0}</div>
                  <div className="text-muted small mt-1">
                    {coursesData?.inscripcionesEsteMes || 0} este mes
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div
                  className="flex-shrink-0"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "12px",
                    backgroundColor: `${secondaryColor}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "10px",
                  }}
                >
                  <Target size={30} color={secondaryColor} />
                </div>
                <div className="flex-grow-1 ms-4">
                  <div className="text-muted small">Total Leads</div>
                  <div className="h3 mb-0">{leadsData?.totalLeads || 0}</div>
                  <div className="text-muted small mt-1">
                    {leadsData?.tasaConversion || 0}% conversión
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sección: Asociados */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="mb-0">Asociados</h2>
        </div>
        <div className="col-lg-4 col-md-6 mb-4">
          <Card>
            <CardHeader title="Distribución Activos/Inactivos" />
            <CardBody>
              {associatesData && (
                <ReactApexChart
                  options={associatesDonutOptions}
                  series={associatesDonutSeries}
                  type="donut"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-8 col-md-6 mb-4">
          <Card>
            <CardHeader title="Evolución de Asociados (12 meses)" />
            <CardBody>
              {associatesData && (
                <ReactApexChart
                  options={associatesLineOptions}
                  series={associatesLineSeries}
                  type="line"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <UserCheck size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{associatesData?.activos || 0}</div>
              <div className="text-muted">Activos</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <UserX size={40} color="#e0e0e0" className="mb-2" />
              <div className="h4 mb-0">{associatesData?.inactivos || 0}</div>
              <div className="text-muted">Inactivos</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <TrendingUp size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{associatesData?.nuevosEsteMes || 0}</div>
              <div className="text-muted">Nuevos este mes</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Users size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{associatesData?.totalAsociados || 0}</div>
              <div className="text-muted">Total</div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sección: Pagos */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="mb-0">Pagos y Cuotas</h2>
        </div>
        <div className="col-lg-8 col-md-6 mb-4">
          <Card>
            <CardHeader title="Evolución de Ingresos (12 meses)" />
            <CardBody>
              {paymentsData && (
                <ReactApexChart
                  options={paymentsLineOptions}
                  series={paymentsLineSeries}
                  type="line"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-4 col-md-6 mb-4">
          <Card>
            <CardHeader title="Estado de Pagos" />
            <CardBody>
              {paymentsData && (
                <ReactApexChart
                  options={paymentStatusOptions}
                  series={paymentStatusSeries}
                  type="donut"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-6 col-md-6 mb-4">
          <Card>
            <CardHeader title="Método de Pago" />
            <CardBody>
              {paymentsData && (
                <ReactApexChart
                  options={paymentMethodOptions}
                  series={paymentMethodSeries}
                  type="bar"
                  height={250}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-6 col-md-6 mb-4">
          <Card>
            <CardHeader title="Próximos Pagos (7 días)" />
            <CardBody>
              {paymentsData?.proximosPagos?.length > 0 ? (
                <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                  {paymentsData.proximosPagos.map((pago, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                    >
                      <div>
                        <div className="font-weight-bold">{pago.nombre}</div>
                        <div className="text-muted small">{pago.email}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-weight-bold">
                          {new Date(pago.fecha).toLocaleDateString("es-ES")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  No hay pagos próximos a vencer
                </div>
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <CreditCard size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{paymentsData?.porcentajeMigrado || 0}%</div>
              <div className="text-muted">Migrados a tarjeta</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <DollarSign size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{paymentsData?.pagosPendientes || 0}</div>
              <div className="text-muted">Pagos pendientes</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Calendar size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{paymentsData?.pagosCompletados || 0}</div>
              <div className="text-muted">Pagos completados</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <TrendingUp size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">
                {paymentsData?.recaudadoEsteAnio?.toFixed(0) || 0}€
              </div>
              <div className="text-muted">Recaudado este año</div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sección: Cursos */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="mb-0">Cursos e Inscripciones</h2>
        </div>
        <div className="col-lg-6 col-md-6 mb-4">
          <Card>
            <CardHeader title="Top 5 Cursos Más Populares" />
            <CardBody>
              {coursesData && (
                <ReactApexChart
                  options={topCoursesOptions}
                  series={topCoursesSeries}
                  type="bar"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-6 col-md-6 mb-4">
          <Card>
            <CardHeader title="Evolución de Inscripciones (12 meses)" />
            <CardBody>
              {coursesData && (
                <ReactApexChart
                  options={coursesLineOptions}
                  series={coursesLineSeries}
                  type="line"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-4 col-md-6 mb-4">
          <Card>
            <CardHeader title="Estado de Inscripciones" />
            <CardBody>
              {coursesData && (
                <ReactApexChart
                  options={inscriptionStatusOptions}
                  series={inscriptionStatusSeries}
                  type="donut"
                  height={250}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-2 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <BookOpen size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{coursesData?.totalCursos || 0}</div>
              <div className="text-muted">Total cursos</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-2 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <TrendingUp size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{coursesData?.cursosActivos || 0}</div>
              <div className="text-muted">Cursos activos</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-2 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Users size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{coursesData?.totalInscripciones || 0}</div>
              <div className="text-muted">Total inscripciones</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-2 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Target size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{coursesData?.tasaOcupacion || 0}%</div>
              <div className="text-muted">Tasa ocupación</div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sección: Leads y Conversiones */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="mb-0">Leads y Conversiones</h2>
        </div>
        <div className="col-lg-8 col-md-6 mb-4">
          <Card>
            <CardHeader title="Leads vs Nuevos Socios (12 meses)" />
            <CardBody>
              {leadsData && (
                <ReactApexChart
                  options={leadsComparisonOptions}
                  series={leadsComparisonSeries}
                  type="line"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-4 col-md-6 mb-4">
          <Card>
            <CardHeader title="Tasa de Conversión Mensual" />
            <CardBody>
              {leadsData && (
                <ReactApexChart
                  options={conversionBarOptions}
                  series={conversionBarSeries}
                  type="bar"
                  height={300}
                />
              )}
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Target size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{leadsData?.totalLeads || 0}</div>
              <div className="text-muted">Total leads</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <TrendingUp size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{leadsData?.leadsEsteMes || 0}</div>
              <div className="text-muted">Leads este mes</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Users size={40} color={primaryColor} className="mb-2" />
              <div className="h4 mb-0">{leadsData?.nuevosSociosEsteMes || 0}</div>
              <div className="text-muted">Nuevos socios este mes</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <Card>
            <CardBody className="text-center">
              <Target size={40} color={secondaryColor} className="mb-2" />
              <div className="h4 mb-0">{leadsData?.tasaConversion || 0}%</div>
              <div className="text-muted">Tasa de conversión</div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
